import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Share2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export default function MyFiles() {
    const [files, setFiles] = useState([]);
    const refresh = useCallback(async () => {
        try { const { data } = await api.get("/files"); setFiles(data); } catch (e) { /* */ }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const copy = async (token) => {
        const url = `${window.location.origin}/s/${token}`;
        try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
        catch { toast.info(url); }
    };
    const create = async (id) => {
        try { const { data } = await api.post(`/files/${id}/share`); await refresh(); await copy(data.share_token); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    };
    const revoke = async (id) => {
        if (!window.confirm("Revoke share link?")) return;
        try { await api.delete(`/files/${id}/share`); await refresh(); toast.success("Revoked"); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    return (
        <>
            <Helmet><title>My files — OpenCSV</title></Helmet>
            <section className="mx-auto max-w-5xl px-4 md:px-8 py-12">
                <h1 className="font-display font-black tracking-tighter text-4xl">My files</h1>
                <p className="mt-2 text-sm text-ink-soft">Open in the editor, or generate a read-only public link to share.</p>
                <ul className="mt-6 divide-y divide-black/10 border border-black/10 bg-white">
                    {files.map((f) => (
                        <li key={f.file_id} className="flex items-center px-4 py-3 gap-2 flex-wrap" data-testid={`file-${f.file_id}`}>
                            <span className="font-mono text-sm flex-1 min-w-0 truncate">{f.name}</span>
                            {f.share_token && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] ui-heading uppercase tracking-widest bg-signal-green/10 text-signal-green border border-signal-green/40">
                                    <Share2 size={10} /> shared
                                </span>
                            )}
                            <span className="text-xs text-ink-muted hidden sm:inline">{f.size} b</span>
                            <Link to={`/app?file=${f.file_id}`} className="ui-heading text-xs px-3 py-1.5 border border-ink/15 hover:border-cobalt">Open</Link>
                            {f.share_token ? (
                                <>
                                    <button onClick={() => copy(f.share_token)} className="ui-heading text-xs px-3 py-1.5 border border-ink/15 hover:border-cobalt">Copy link</button>
                                    <a href={`/s/${f.share_token}`} target="_blank" rel="noreferrer" className="ui-heading text-xs px-3 py-1.5 border border-ink/15 hover:border-cobalt inline-flex items-center gap-1">
                                        <ExternalLink size={10} /> Preview
                                    </a>
                                    <button onClick={() => revoke(f.file_id)} className="ui-heading text-xs px-3 py-1.5 border border-signal-red/40 text-signal-red">Unshare</button>
                                </>
                            ) : (
                                <button onClick={() => create(f.file_id)} className="ui-heading text-xs px-3 py-1.5 border border-cobalt text-cobalt hover:bg-cobalt hover:text-white">Share</button>
                            )}
                        </li>
                    ))}
                    {files.length === 0 && <li className="p-6 text-center text-ink-muted">No files saved yet.</li>}
                </ul>
            </section>
        </>
    );
}
